import React from 'react'

import { FileCard } from '../cards/FileCard'
import { FileItem } from '../hooks/useFileManagment'

interface FileGridProps {
  files: FileItem[]
  dragOverIndex: number | null
  draggedFiles: (string | number)[]
  selectedFiles: Set<string | number>
  fileRefs: React.MutableRefObject<Map<string | number, HTMLElement>>
  onFileClick: (fileId: string | number, event: React.MouseEvent) => void
  onFileDoubleClick?: (fileId: string | number) => void
  onFileDrop: (event: React.DragEvent, index: number) => void
  onContextMenu: (event: React.MouseEvent, fileId: string | number) => void
  onDragEnd: () => void
  onDragEnter: (event: React.DragEvent) => void
  onDragLeave: () => void
  onDragStart: (event: React.DragEvent, fileId?: string | number) => void
  onDragOver: (event: React.DragEvent, index: number) => void
  trashMode?: boolean
  onCopy?: (file: FileItem) => void
  onDelete?: (file: FileItem) => void
  onDeleteForever?: (file: FileItem) => Promise<void>
  onDownload?: (file: FileItem) => void
  onRename?: (file: FileItem) => void
  onRestore?: (file: FileItem) => Promise<void>
  onToggleFavorite?: (file: FileItem) => void
  onMove?: (file: FileItem) => void
  onSelectionChange?: (fileId: string | number, selected: boolean) => void
}

export const FileGrid: React.FC<FileGridProps> = ({
  files,
  dragOverIndex,
  draggedFiles,
  selectedFiles,
  fileRefs,
  onFileClick,
  onFileDoubleClick,
  onFileDrop,
  onContextMenu,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragStart,
  onDragOver,
  trashMode,
  onCopy,
  onDelete,
  onDeleteForever,
  onDownload,
  onRename,
  onRestore,
  onToggleFavorite,
  onMove,
  onSelectionChange,
}) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 lg:grid-cols-3 2xl:grid-cols-5 gap-3 sm:gap-4">
      {files.map((file, index) => (
        <FileCard
          key={file.id}
          draggedFiles={draggedFiles}
          file={file}
          isDragOver={dragOverIndex === index}
          isDragging={draggedFiles.includes(file.id)}
          isSelected={selectedFiles.has(file.id)}
          trashMode={trashMode}
          innerRef={el => {
            if (el) {
              fileRefs.current.set(file.id, el)
              el.setAttribute('data-file-id', file.id.toString())
            } else {
              fileRefs.current.delete(file.id)
            }
          }}
          isFolderDrop={
            file.type === 'folder' && dragOverIndex === index && draggedFiles.length > 0
          }
          onClick={e => onFileClick(file.id, e)}
          onCopy={() => onCopy?.(file)}
          onDelete={() => onDelete?.(file)}
          onDeleteForever={onDeleteForever}
          onDoubleClick={() => onFileDoubleClick?.(file.id)}
          onDownload={() => onDownload?.(file)}
          onDragEnd={onDragEnd}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragStart={e => onDragStart(e, file.id)}
          onDrop={e => onFileDrop(e, index)}
          onMove={onMove}
          onRename={() => onRename?.(file)}
          onRestore={onRestore}
          onSelectionChange={onSelectionChange}
          onToggleFavorite={() => onToggleFavorite?.(file)}
          onContextMenu={e => {
            e.preventDefault()
            if (!selectedFiles.has(file.id)) {
              onSelectionChange?.(file.id, true)
            }
            onContextMenu(e, file.id)
          }}
          onDragOver={e => {
            e.preventDefault()
            onDragOver(e, index)
          }}
        />
      ))}
    </div>
  )
}
